import React from "react";
import { Environment } from "components/Environment/utils/types";
import {
  NodePortsSection,
  type NodePortsSectionProps,
} from "components/NodeDetail/BrevConnect/NodePortsSection";
import {
  NodeSecureLinksSection,
  type NodeSecureLinksSectionProps,
} from "components/NodeDetail/BrevConnect/NodeSecureLinksSection";
import { useEnvironmentNetworkAccess } from "hooks/environment/useEnvironmentNetworkAccess";
import type { DisplayPort } from "utils/environment/networkAccessFallback";
import { getEnvironmentStatus } from "components/Environment/utils/environtmentUtils";
import { WorkspaceStatus } from "models/Workspace.model";
import { NodeDetailSection } from "components/NodeDetail/shared/NodeDetailSection";

interface SkybridgeHTTPPortsProps {
  environment: Environment;
  accessKind?: "all" | "applications" | "ports";
}

export interface SkybridgeViewProps {
  httpPorts: DisplayPort[];
  tcpPorts: DisplayPort[];
  isLoadingPorts?: boolean;
  canModifyNetworkAccess?: boolean;
  canManagePort?: (port: DisplayPort) => boolean;
  onOpenHTTPPort: NodeSecureLinksSectionProps["onOpenHTTPPort"];
  onSetHTTPPortTarget: NodeSecureLinksSectionProps["onSetPortTarget"];
  onSetHTTPPortAllowedSources: NodeSecureLinksSectionProps["onSetPortAllowedSources"];
  onSetHTTPPortAccess: NodeSecureLinksSectionProps["onSetHTTPPortAccess"];
  onCloseHTTPPort: NodeSecureLinksSectionProps["onClosePort"];
  onOpenPort: NodePortsSectionProps["onOpenPort"];
  onSetPortTarget: NodePortsSectionProps["onSetPortTarget"];
  onSetPortAllowedSources: NodePortsSectionProps["onSetPortAllowedSources"];
  onClosePort: NodePortsSectionProps["onClosePort"];
  showMicroK8sNodePortNotice?: boolean;
  accessKind?: "all" | "applications" | "ports";
}

export const SkybridgeView: React.FC<SkybridgeViewProps> = ({
  httpPorts,
  tcpPorts,
  isLoadingPorts = false,
  canModifyNetworkAccess = true,
  canManagePort = () => true,
  onOpenHTTPPort,
  onSetHTTPPortTarget,
  onSetHTTPPortAllowedSources,
  onSetHTTPPortAccess,
  onCloseHTTPPort,
  onOpenPort,
  onSetPortTarget,
  onSetPortAllowedSources,
  onClosePort,
  showMicroK8sNodePortNotice = false,
  accessKind = "all",
}) => (
  <>
    {accessKind !== "ports" && (
      <NodeDetailSection title="HTTP Ports" className="mt-8">
        <NodeSecureLinksSection
          ports={httpPorts}
          isLoading={isLoadingPorts}
          canOpenSecureLink={canModifyNetworkAccess}
          canManagePort={canManagePort}
          onOpenHTTPPort={onOpenHTTPPort}
          onSetPortTarget={onSetHTTPPortTarget}
          onSetPortAllowedSources={onSetHTTPPortAllowedSources}
          onSetHTTPPortAccess={onSetHTTPPortAccess}
          onClosePort={onCloseHTTPPort}
          showMicroK8sNodePortNotice={showMicroK8sNodePortNotice}
        />
      </NodeDetailSection>
    )}

    {accessKind !== "applications" && (
      <NodeDetailSection title="TCP/UDP Ports">
        <NodePortsSection
          ports={tcpPorts}
          isLoading={isLoadingPorts}
          canOpenPort={canModifyNetworkAccess}
          canManagePort={canManagePort}
          onOpenPort={onOpenPort}
          onSetPortTarget={onSetPortTarget}
          onSetPortAllowedSources={onSetPortAllowedSources}
          onClosePort={onClosePort}
        />
      </NodeDetailSection>
    )}
  </>
);

const SkybridgeHTTPPorts: React.FC<SkybridgeHTTPPortsProps> = ({
  environment,
  accessKind = "all",
}) => {
  const {
    httpPorts,
    tcpPorts,
    openPort,
    openHTTPPort,
    isLoadingPorts,
    setPortTarget,
    setPortAllowedSources,
    setHTTPPortAccess,
    closePort,
    canManagePort,
  } = useEnvironmentNetworkAccess(environment);
  const canModifyNetworkAccess =
    getEnvironmentStatus(environment) !== WorkspaceStatus.Deleting;
  const showMicroK8sNodePortNotice = Boolean(
    environment.build?.vm?.k8s && !environment.build.vm.k8s.isDisabled,
  );

  return (
    <SkybridgeView
      httpPorts={httpPorts}
      tcpPorts={tcpPorts}
      isLoadingPorts={isLoadingPorts}
      canModifyNetworkAccess={canModifyNetworkAccess}
      canManagePort={(port) => canModifyNetworkAccess && canManagePort(port)}
      onOpenHTTPPort={async (portNumber, options) => {
        if (!canModifyNetworkAccess) return;
        await openHTTPPort(portNumber, {
          httpProtocol: options.httpProtocol,
          customHostname: options.customHostname,
          authorizedEmails: options.authorizedEmails,
          allowPublicUnauthenticated: options.allowPublicUnauthenticated,
        });
      }}
      onSetHTTPPortTarget={async (portId, portNumber) => {
        if (!canModifyNetworkAccess) return;
        await setPortTarget(portId, portNumber);
      }}
      onSetHTTPPortAllowedSources={async (portId, allowedSources) => {
        if (!canModifyNetworkAccess) return;
        await setPortAllowedSources(portId, allowedSources);
      }}
      onSetHTTPPortAccess={async (
        portId,
        authorizedEmails,
        allowPublicUnauthenticated,
      ) => {
        if (!canModifyNetworkAccess) return;
        await setHTTPPortAccess(
          portId,
          authorizedEmails,
          allowPublicUnauthenticated,
        );
      }}
      onCloseHTTPPort={async (portId) => {
        if (!canModifyNetworkAccess) return;
        await closePort(portId);
      }}
      onOpenPort={async (
        portNumber,
        protocol,
        allowedSources,
        toPortNumber,
      ) => {
        if (!canModifyNetworkAccess) return;
        await openPort(portNumber, protocol, allowedSources, toPortNumber);
      }}
      onSetPortTarget={async (portId, portNumber) => {
        if (!canModifyNetworkAccess) return;
        await setPortTarget(portId, portNumber);
      }}
      onSetPortAllowedSources={async (portId, allowedSources) => {
        if (!canModifyNetworkAccess) return;
        await setPortAllowedSources(portId, allowedSources);
      }}
      onClosePort={async (portId) => {
        if (!canModifyNetworkAccess) return;
        await closePort(portId);
      }}
      showMicroK8sNodePortNotice={showMicroK8sNodePortNotice}
      accessKind={accessKind}
    />
  );
};

export default SkybridgeHTTPPorts;
